import Type from "../definitions/Type";
import Severity from "../definitions/Severity";
import {NotificationConfig, NotificationState} from "../definitions/Config";

import Chat from "../types/Chat";
import BrowserUpgrade from "../types/BrowserUpgrade";
import NoDomain from "../types/NoDomain";
import MaintenanceMode from "../types/MaintenanceMode";
import Congrats from "../types/Congrats";
import NotPublished from "../types/NotPublished";
import Guidance from "../types/Guidance";
import EMP from "../types/EMP";
import XingVouchers from "../types/XingVouchers";
import WebComponentUpsell from "../types/WebComponentUpsell";
import ExternalWebsiteImportStarted from "../types/ExternalWebsiteImportStarted";
import ExternalWebsiteImportSkipped from "../types/ExternalWebsiteImportSkipped";
import ExternalWebsiteImportFinished from "../types/ExternalWebsiteImportFinished";
import SectionsAddGroup from "../types/SectionsAddGroup";
import RankingCoach from "../types/RankingCoach";
import AviaryShutdown from "../types/AviaryShutdown";

/**
 * Factory to merge a predefined Notification Config
 * with a dynamical Notification State
 */
export default class ConfigFactory {

    /**
     * Get the default Configuration
     *
     * @returns {NotificationConfig}
     */
    static getDefault(): NotificationConfig {
        return {
            type: null,
            testId: "",
            trackingId: "",

            title: "",
            content: "",
            icon: null,
            severity: Severity.NORMAL,

            mainAction: null,
            dismissAction: null,

            bubbleOptions: {
                closable: true,
                autoHide: true,
                hideDelay: 7000
            },

            cardOptions: {
                closable: true,
                hidden: false
            }
        };
    }

    /**
     * Get the default Configuration for a notification type
     *
     * @param   {Type} type
     * @returns {NotificationConfig}
     * @throws  {Error}
     */
    static getConfig(type: Type): NotificationConfig {
        let notificationConfig: NotificationConfig;

        switch (type) {
            case Type.CHAT:
                notificationConfig = Chat;
                break;

            case Type.BROWSER_UPGRADE:
                notificationConfig = BrowserUpgrade;
                break;

            case Type.NO_DOMAIN:
                notificationConfig = NoDomain;
                break;

            case Type.MAINTENANCE_MODE:
                notificationConfig = MaintenanceMode;
                break;

            case Type.CONGRATS:
                notificationConfig = Congrats;
                break;

            case Type.NOT_PUBLISHED:
                notificationConfig = NotPublished;
                break;

            case Type.GUIDANCE:
                notificationConfig = Guidance;
                break;

            case Type.EMP:
                notificationConfig = EMP;
                break;

            case Type.XING_VOUCHERS:
                notificationConfig = XingVouchers;
                break;

            case Type.WEB_COMPONENT_UPSELL:
                notificationConfig = WebComponentUpsell;
                break;

            case Type.EXTERNAL_WEBSITE_IMPORT_STARTED:
                notificationConfig = ExternalWebsiteImportStarted;
                break;

            case Type.EXTERNAL_WEBSITE_IMPORT_SKIPPED:
                notificationConfig = ExternalWebsiteImportSkipped;
                break;

            case Type.EXTERNAL_WEBSITE_IMPORT_FINISHED:
                notificationConfig = ExternalWebsiteImportFinished;
                break;

            case Type.SECTIONS_ADD_GROUP:
                notificationConfig = SectionsAddGroup;
                break;

            case Type.RANKING_COACH:
                notificationConfig = RankingCoach;
                break;

            case Type.AVIARY_SHUTDOWN:
                notificationConfig = AviaryShutdown;
                break;

            default:
                throw new Error("Notification Model of type " + type + " doesn't exist");
        }
        return ConfigFactory.merge(
            ConfigFactory.getDefault(),
            notificationConfig
        );
    }

    /**
     * Merge two Config objects together
     *
     * @param {NotificationConfig} initialConfig
     * @param {NotificationConfig} updatedConfig
     * @returns {NotificationConfig}
     */
    static merge(initialConfig: NotificationConfig, updatedConfig: NotificationConfig): NotificationConfig {
        let notificationConfig = Object.assign({},
            initialConfig,
            updatedConfig
        );
        notificationConfig.bubbleOptions = Object.assign({},
            initialConfig.bubbleOptions,
            updatedConfig.bubbleOptions
        );
        notificationConfig.cardOptions = Object.assign({},
            initialConfig.cardOptions,
            updatedConfig.cardOptions
        );
        return notificationConfig;
    }

    /**
     * Generates the last Config version using State data
     *
     * @param   {NotificationState} notificationState
     * @returns {NotificationConfig}
     */
    static factory(notificationState: NotificationState): NotificationConfig {
        let notificationConfig = Object.assign({},
            ConfigFactory.getConfig(notificationState.type),
            notificationState
        );
        if (typeof notificationConfig.onFactory === "function") {
            notificationConfig = notificationConfig.onFactory(notificationConfig);
        }
        return notificationConfig;
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/toolbar/notificationNG/utils/ConfigFactory.ts
 **/